import { initAxios } from '../../../utils/axiosHelper';
import {
  BrokerPaginationParams,
  CreateBrokerResponse,
  IBroker,
  PaginatedResponse,
} from './brokers';

const defaultPagination = {
  currentPage: 1,
  totalPages: 0,
  totalItems: 0,
  itemsPerPage: 10,
  hasNextPage: false,
  hasPreviousPage: false,
};

export const getProductById = async (id?: string): Promise<IBroker | null> => {
  try {
    if (!id) {
      return null;
    }
    const instance = await initAxios();
    if (!instance) {
      return null;
    }
    const resp = await instance.get(`/bite-db/broker/${id}`);
    return resp.data?.data || null;
  } catch (err) {
    console.error('Error fetching broker by ID:', err);
    return null;
  }
};

export const getBrokersByGroupId = async (id?: string): Promise<IBroker[]> => {
  try {
    if (!id) {
      return [];
    }

    const instance = await initAxios();
    if (!instance) {
      return [];
    }

    const resp = await instance.get(`/bite-db/brokers/group/${id}`);

    return resp.data?.data || [];
  } catch (err) {
    console.error('Error fetching brokers:', err);
    return [];
  }
};

export const getBrokersByGroupIdPagination = async (
  id?: string,
  params?: Partial<BrokerPaginationParams>,
): Promise<PaginatedResponse<IBroker>> => {
  try {
    if (!id) {
      return { data: [], pagination: defaultPagination };
    }

    const instance = await initAxios();
    if (!instance) {
      return { data: [], pagination: defaultPagination };
    }

    const queryParams = new URLSearchParams({
      page: (params?.page || 1).toString(),
      limit: (params?.limit || 10).toString(),
      sortBy: params?.sortBy || 'created_at',
      sortOrder: params?.sortOrder || 'desc',
      search: params?.search || '',
      type: params?.type || '',
    });

    const resp = await instance.get(
      `/contacts/group/${id}/brokers?${queryParams.toString()}`,
    );

    return {
      data: resp.data?.data || [],
      pagination: resp.data?.pagination || defaultPagination,
    };
  } catch (err) {
    console.error('Error fetching paginated brokers:', err);
    return { data: [], pagination: defaultPagination };
  }
};

export const createBroker = async (
  broker: Partial<CreateBrokerResponse>,
  teamId?: string,
): Promise<CreateBrokerResponse> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      return { success: false, message: 'Failed to initialize API client' };
    }

    const resp = await instance.post(
      '/bite-db/broker',
      JSON.stringify(broker),
      {
        headers: {
          'x-group-id': teamId || '',
        },
      },
    );
    return resp.data;
  } catch (err: any) {
    return {
      success: false,
      message: err.response?.data?.message || 'Failed to create broker',
    };
  }
};

export const updateBroker = async (
  id: string,
  brokerData: Partial<IBroker>,
): Promise<{ success: boolean; data?: IBroker; message?: string }> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      return { success: false, message: 'Failed to initialize API client' };
    }

    const resp = await instance.put(
      `/bite-db/broker/${id}`,
      JSON.stringify(brokerData),
    );
    return { success: true, data: resp.data.data };
  } catch (err: any) {
    return {
      success: false,
      message: err.response?.data?.message || 'Failed to update broker',
    };
  }
};

export const deleteProduct = async (
  id: string,
): Promise<CreateBrokerResponse> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      return { success: false, message: 'Failed to initialize API client' };
    }

    const resp = await instance.delete(`/bite-db/broker/${id}`);
    return {
      success: true,
      data: resp.data.data,
      message: 'Deleted broker successfully',
    };
  } catch (err: any) {
    return {
      success: false,
      message: err.response?.data?.message || 'Failed to delete broker',
    };
  }
};
